import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, FormControlLabel, Grid, Paper, Popover, Stack, Switch, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { api } from "../api";
import AppShell from "./AppShell";
import ImageWithFallback from "./ImageWithFallback";
import usePageTitle from "../hooks/usePageTitle";
import { buildItemUrl } from "../utils/itemUrl";

const ALLOWED_REACTIONS = [
  "\uD83D\uDC4D", // thumbs up
  "\uD83D\uDD25", // fire
  "\uD83D\uDC4F", // clapping
  "\uD83D\uDE2E", // surprised
  "\u2764\uFE0F", // heart
  "\uD83D\uDCAF", // 100
  "\uD83D\uDC40", // eyes
  "\uD83D\uDE0D", // heart eyes
  "\uD83C\uDFC6", // trophy
];

const panelSx = {
  p: { xs: 2, md: 2.5 },
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const cardSx = {
  p: 1.75,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  height: "100%",
  borderRadius: 2,
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#f9fbfd",
};

const principles = [
  {
    title: "Friends first",
    detail: "Collector Circles should stay intentional and centered on people you know.",
  },
  {
    title: "Collections first",
    detail: "Profiles, wishlists, recent pickups, and milestones are the core experience.",
  },
  {
    title: "No endless scrolling",
    detail: "Activity should be useful, calm, and easy to leave when you are done checking in.",
  },
  {
    title: "Privacy controls matter",
    detail: "Collectors should control what is shared with friends and what remains private.",
  },
];

function Section({ title, subtitle, action, children }) {
  return (
    <Paper elevation={0} sx={panelSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "primary.main", mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 760 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && (
          <Box sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 0 }}>
            {action}
          </Box>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function getCollectorName(activity) {
  return activity?.username || activity?.name || "A collector";
}

function getRequestCollectorName(request) {
  return request?.username || request?.name || "A collector";
}

function getFriendCollectorName(friend) {
  return friend?.display_name || friend?.name || friend?.username || "A collector";
}

function getActivityTitle(activity) {
  const collector = getCollectorName(activity);
  const itemName = activity?.item?.name || activity?.metadata?.item_name || "an item";
  if (activity?.activity_type === "wishlist_added") {
    return `${collector} added ${itemName} to their wishlist`;
  }
  if (activity?.activity_type === "milestone") {
    return `${collector} reached a collection milestone`;
  }
  return `${collector} added ${itemName} to their collection`;
}

function getActivityDetail(activity) {
  if (activity?.activity_type === "wishlist_added") return "Wishlist update";
  if (activity?.activity_type === "milestone") return activity?.metadata?.label || "Collection milestone";
  return "Recent pickup";
}

function formatActivityDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function CommunityPage() {
  usePageTitle("Community");
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAuthError, setIsAuthError] = useState(false);
  const [friendRequests, setFriendRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestBusy, setRequestBusy] = useState({});
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(true);
  const [friendSearch, setFriendSearch] = useState("");
  const [includeMyActivity, setIncludeMyActivity] = useState(false);
  const [reactionBusy, setReactionBusy] = useState({});
  const [reactionMenu, setReactionMenu] = useState({ anchorEl: null, activity: null });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadActivity = async () => {
      try {
        setLoading(true);
        setError("");
        setIsAuthError(false);
        const { data } = await api.get(`/api/community/feed${includeMyActivity ? "?include_self=1" : ""}`);
        if (!mounted) return;
        setActivity(Array.isArray(data?.activity) ? data.activity : []);
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 401) {
          setIsAuthError(true);
          setError("Sign in to see collection activity from your friends.");
        } else {
          setIsAuthError(false);
          setError(e?.response?.data?.message || e?.response?.data?.error || "Could not load community activity.");
        }
        setActivity([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadActivity();
    return () => { mounted = false; };
  }, [includeMyActivity]);

  useEffect(() => {
    let mounted = true;
    const loadFriendData = async () => {
      try {
        setRequestsLoading(true);
        setFriendsLoading(true);
        const [requestsRes, friendsRes] = await Promise.all([
          api.get("/api/friends/requests"),
          api.get("/api/friends"),
        ]);
        if (!mounted) return;
        setFriendRequests(Array.isArray(requestsRes?.data?.requests) ? requestsRes.data.requests : []);
        setFriends(Array.isArray(friendsRes?.data?.friends) ? friendsRes.data.friends : []);
      } catch {
        if (mounted) {
          setFriendRequests([]);
          setFriends([]);
        }
      } finally {
        if (mounted) {
          setRequestsLoading(false);
          setFriendsLoading(false);
        }
      }
    };
    loadFriendData();
    return () => { mounted = false; };
  }, []);

  const hasActivity = useMemo(() => activity.length > 0, [activity]);
  const filteredFriends = useMemo(() => {
    const needle = friendSearch.trim().toLowerCase();
    if (!needle) return friends;
    return friends.filter((friend) => {
      const haystack = [friend?.username, friend?.display_name, friend?.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [friendSearch, friends]);

  const handleFriendRequestAction = async (request, action) => {
    const friendshipId = Number(request?.friendship_id);
    if (!Number.isFinite(friendshipId) || friendshipId <= 0) return;

    setRequestBusy((current) => ({ ...current, [friendshipId]: true }));
    try {
      const endpoint = action === "accept" ? "/api/friends/accept" : "/api/friends/decline";
      await api.post(endpoint, { friendship_id: friendshipId });
      setFriendRequests((current) => current.filter((item) => Number(item.friendship_id) !== friendshipId));
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally {
      setRequestBusy((current) => ({ ...current, [friendshipId]: false }));
    }
  };

  const handleReaction = async (entry, emoji) => {
    const activityId = Number(entry?.activity_id);
    if (!Number.isFinite(activityId) || activityId <= 0) return;

    const busyKey = `${activityId}:${emoji}`;
    setReactionBusy((current) => ({ ...current, [busyKey]: true }));
    try {
      const { data } = await api.post(`/api/community/activity/${activityId}/reactions`, { emoji });
      setActivity((current) => current.map((item) => (
        Number(item.activity_id) === activityId
          ? {
              ...item,
              reaction_counts: data?.reaction_counts || {},
              my_reactions: Array.isArray(data?.my_reactions) ? data.my_reactions : [],
            }
          : item
      )));
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally {
      setReactionBusy((current) => ({ ...current, [busyKey]: false }));
    }
  };

  const openReactionMenu = (event, entry) => {
    setReactionMenu({ anchorEl: event.currentTarget, activity: entry });
  };

  const closeReactionMenu = () => {
    setReactionMenu({ anchorEl: null, activity: null });
  };

  const friendRequestsSection = (
    <Section title="Friend Requests" subtitle="Review collectors who want to join your Collector Circle.">
      {requestsLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading friend requests...</Typography>
        </Box>
      ) : friendRequests.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No pending friend requests.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {friendRequests.map((request) => {
            const friendshipId = Number(request?.friendship_id);
            const isBusy = Boolean(requestBusy[friendshipId]);
            const name = getRequestCollectorName(request);
            return (
              <Paper
                key={request.friendship_id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: "rgba(12, 35, 64, 0.08)",
                  backgroundColor: "#f9fbfd",
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center", md: "flex-start" },
                  justifyContent: "space-between",
                  gap: 1.5,
                  flexDirection: { xs: "column", sm: "row", md: "column" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid rgba(12, 35, 64, 0.08)",
                      backgroundColor: "#eef3f8",
                    }}
                  >
                    <ImageWithFallback
                      src={request?.profile_picture || "/logos/default-avatar.jpg"}
                      alt={name}
                      placeholderSrc="/logos/default-avatar.jpg"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f" }} noWrap>
                      {name}
                    </Typography>
                    {request?.username && request?.name && request.username !== request.name && (
                      <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                        @{request.username}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                  <Button
                    variant="contained"
                    disabled={isBusy}
                    onClick={() => handleFriendRequestAction(request, "accept")}
                    sx={{ fontWeight: 800, flex: 1 }}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={isBusy}
                    onClick={() => handleFriendRequestAction(request, "decline")}
                    sx={{ fontWeight: 800, flex: 1 }}
                  >
                    Decline
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Section>
  );

  const friendsSection = (
    <Section
      title="Friends"
      subtitle="Search your Collector Circle."
      action={
        <Button component={RouterLink} to="/accounts" variant="contained" sx={{ width: { xs: "100%", sm: "auto" }, fontWeight: 800, whiteSpace: "nowrap" }}>
          Find Collectors
        </Button>
      }
    >
      <TextField
        fullWidth
        size="small"
        placeholder="Search friends by username or name..."
        value={friendSearch}
        onChange={(e) => setFriendSearch(e.target.value)}
        sx={{ mb: 1.5 }}
      />
      {friendsLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading friends...</Typography>
        </Box>
      ) : friends.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No friends yet. Find collectors to build your circle.
        </Typography>
      ) : filteredFriends.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No friends match that search.
        </Typography>
      ) : (
        <Grid container spacing={1.5}>
          {filteredFriends.map((friend) => {
            const name = getFriendCollectorName(friend);
            const username = typeof friend?.username === "string" ? friend.username.trim() : "";
            const clickableProps = username
              ? {
                  component: RouterLink,
                  to: `/mycollection/${encodeURIComponent(username)}`,
                }
              : {};
            return (
              <Grid item xs={12} sm={6} md={12} key={friend.friendship_id || friend.user_id}>
                <Paper
                  variant="outlined"
                  {...clickableProps}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: "rgba(12, 35, 64, 0.08)",
                    backgroundColor: "#f9fbfd",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    width: "100%",
                    minWidth: 0,
                    color: "inherit",
                    textDecoration: "none",
                    cursor: username ? "pointer" : "default",
                    transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                    ...(username
                      ? {
                          "&:hover": {
                            borderColor: "rgba(12, 35, 64, 0.22)",
                            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.10)",
                            transform: { md: "translateY(-1px)" },
                          },
                        }
                      : {}),
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "1px solid rgba(12, 35, 64, 0.08)",
                      backgroundColor: "#eef3f8",
                    }}
                  >
                    <ImageWithFallback
                      src={friend?.profile_picture || friend?.avatar_url || "/logos/default-avatar.jpg"}
                      alt={name}
                      placeholderSrc="/logos/default-avatar.jpg"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#10233f" }} noWrap>
                      {name}
                    </Typography>
                    {friend?.username && (
                      <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                        @{friend.username}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Section>
  );

  const activitySection = (
    <Section
      title="Friends Activity"
      subtitle="Recent pickups, wishlist updates, and milestones from your Collector Circle."
      action={
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="flex-end"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={includeMyActivity}
                onChange={(event) => setIncludeMyActivity(event.target.checked)}
                size="small"
              />
            }
            label="Include my activity"
            sx={{
              m: 0,
              color: "text.secondary",
              ".MuiFormControlLabel-label": { fontSize: "0.9rem", fontWeight: 700 },
            }}
          />
          <Button component={RouterLink} to="/accounts" variant="contained" sx={{ width: { xs: "100%", sm: "auto" }, fontWeight: 800, whiteSpace: "nowrap" }}>
            Find Collectors
          </Button>
        </Stack>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading collection activity...</Typography>
        </Box>
      ) : error || !hasActivity ? (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            borderColor: "rgba(12, 35, 64, 0.08)",
            backgroundColor: "#f9fbfd",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#10233f", mb: 0.75 }}>
            Build your Collector Circle
          </Typography>
          <Typography sx={{ color: "text.secondary", maxWidth: 620, mx: "auto", mb: 2 }}>
            {error || "Add friends to see their recent pickups, wishlist updates, and milestones here."}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} justifyContent="center">
            <Button component={RouterLink} to="/accounts" variant="contained" sx={{ fontWeight: 800 }}>
              Find Collectors
            </Button>
            {isAuthError && (
              <Button onClick={() => navigate("/login")} variant="outlined" sx={{ fontWeight: 800 }}>
                Sign In
              </Button>
            )}
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {activity.map((entry) => {
            const item = entry?.item;
            const reactionCounts = entry?.reaction_counts || {};
            const myReactions = Array.isArray(entry?.my_reactions) ? entry.my_reactions : [];
            const usedReactions = ALLOWED_REACTIONS.filter((emoji) => Number(reactionCounts[emoji] || 0) > 0);
            return (
              <Grid item xs={12} key={entry.activity_id}>
                <Paper variant="outlined" sx={{ ...cardSx }}>
                  <Box
                    component={item?.item_id ? RouterLink : "div"}
                    to={item?.item_id ? buildItemUrl(item) : undefined}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        flexShrink: 0,
                        borderRadius: 1.75,
                        overflow: "hidden",
                        border: "1px solid rgba(12, 35, 64, 0.08)",
                        backgroundColor: "#eef3f8",
                      }}
                    >
                      <ImageWithFallback
                        src={item?.image_url || entry?.profile_picture || "/logos/default-item.jpg"}
                        alt={item?.name || getCollectorName(entry)}
                        placeholderSrc="/logos/default-item.jpg"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.4 }}>
                        {getActivityTitle(entry)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                        {getActivityDetail(entry)}
                        {formatActivityDate(entry?.created_at) ? ` - ${formatActivityDate(entry.created_at)}` : ""}
                      </Typography>
                      {(item?.category || item?.brand) && (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
                          {[item.brand, item.category].filter(Boolean).join(" | ")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25, pl: { xs: 0, sm: "88px" }, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={(event) => openReactionMenu(event, entry)}
                      sx={{ minWidth: 0, px: 1.25, fontWeight: 800, lineHeight: 1.2 }}
                    >
                      React
                    </Button>
                    {usedReactions.map((emoji) => {
                      const count = Number(reactionCounts[emoji] || 0);
                      const selected = myReactions.includes(emoji);
                      const reactionKey = `${entry.activity_id}:${emoji}`;
                      return (
                        <Button
                          key={emoji}
                          size="small"
                          variant={selected ? "contained" : "outlined"}
                          color={selected ? "secondary" : "inherit"}
                          disabled={Boolean(reactionBusy[reactionKey])}
                          onClick={() => handleReaction(entry, emoji)}
                          sx={{ minWidth: 0, px: 1.25, fontWeight: 800, lineHeight: 1.2 }}
                        >
                          {emoji} {count}
                        </Button>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Section>
  );

  const principlesSection = (
    <Section title="Community Principles">
      <Grid container spacing={1.5}>
        {principles.map((principle) => (
          <Grid item xs={12} sm={6} md={12} key={principle.title}>
            <Paper variant="outlined" sx={cardSx}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.75 }}>
                {principle.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                {principle.detail}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Section>
  );

  return (
    <AppShell>
      <Box
        sx={{
          width: "100%",
          maxWidth: 1180,
          mx: "auto",
          px: { xs: 2, sm: 2.5, md: 1 },
          py: { xs: 1, md: 1.5 },
          boxSizing: "border-box",
        }}
      >
        <Stack spacing={{ xs: 2, md: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3 },
              background: "linear-gradient(135deg, #13233a 0%, #1a3150 100%)",
              borderColor: "rgba(255,255,255,0.08)",
            }}
          >
            <Chip
              label="Collector Community"
              size="small"
              sx={{
                mb: 1.75,
                fontWeight: 700,
                backgroundColor: "rgba(255,255,255,0.10)",
                color: "#ffffff",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            />
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, lineHeight: 1.08, color: "#ffffff", mb: 1, maxWidth: 760 }}
            >
              Keep up with the collectors you know
            </Typography>
            <Typography
              sx={{
                maxWidth: 760,
                color: "rgba(255,255,255,0.76)",
                fontSize: { xs: "0.98rem", md: "1.03rem" },
                lineHeight: 1.65,
                mb: 2.25,
              }}
            >
              Community at The Collector Stop is built around friends, collection activity,
              recent pickups, wishlist updates, and milestones.
            </Typography>
            <Button
              component={RouterLink}
              to="/accounts"
              variant="contained"
              sx={{
                fontWeight: 800,
                px: 2.25,
                boxShadow: "none",
                backgroundColor: "#ffffff",
                color: "#0b1220",
                "&:hover": { boxShadow: "none", backgroundColor: "rgba(255,255,255,0.92)" },
              }}
            >
              Find Collectors
            </Button>
          </Paper>

          <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 2.5 }} alignItems="flex-start">
            <Box sx={{ width: { xs: "100%", md: "33.333%" }, minWidth: 0 }}>
              <Stack
                spacing={{ xs: 2, md: 2.5 }}
                sx={{
                  width: "100%",
                  position: { md: "sticky" },
                  top: { md: "calc(var(--tcs-header-offset, 120px) + 16px)" },
                }}
              >
                {friendRequestsSection}
                {friendsSection}
                <Box sx={{ display: { xs: "none", md: "block" } }}>{principlesSection}</Box>
              </Stack>
            </Box>
            <Box sx={{ width: { xs: "100%", md: "66.667%" }, minWidth: 0 }}>
              {activitySection}
            </Box>
            <Box sx={{ width: "100%", minWidth: 0, display: { xs: "block", md: "none" } }}>
              {principlesSection}
            </Box>
          </Stack>
        </Stack>
      </Box>
      <Popover
        open={Boolean(reactionMenu.anchorEl)}
        anchorEl={reactionMenu.anchorEl}
        onClose={closeReactionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 0.75,
            borderRadius: 2,
            border: "1px solid rgba(12, 35, 64, 0.10)",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.16)",
          },
        }}
      >
        <Stack direction="row" spacing={0.5}>
          {ALLOWED_REACTIONS.map((emoji) => {
            const entry = reactionMenu.activity;
            const reactionKey = entry ? `${entry.activity_id}:${emoji}` : emoji;
            const selected = Array.isArray(entry?.my_reactions) && entry.my_reactions.includes(emoji);
            return (
              <Button
                key={emoji}
                size="small"
                variant={selected ? "contained" : "text"}
                color={selected ? "secondary" : "inherit"}
                disabled={!entry || Boolean(reactionBusy[reactionKey])}
                onClick={() => {
                  if (entry) handleReaction(entry, emoji);
                  closeReactionMenu();
                }}
                sx={{ minWidth: 42, fontSize: "1.1rem", lineHeight: 1.2 }}
              >
                {emoji}
              </Button>
            );
          })}
        </Stack>
      </Popover>
    </AppShell>
  );
}
